import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthContextType, LoginCredentials, RegisterData, AuthUser } from '../types/Auth';
import { apiService } from '../services/ApiService';

// Estado inicial
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

// Tipos de ações
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: AuthUser; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: AuthUser }
  | { type: 'AUTH_ERROR' };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    default:
      return state;
  }
};

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar token no localStorage ao inicializar
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // Verificar se o token é válido buscando o perfil do usuário
          const response = await apiService.getProfile();
          
          if (response.success && response.data) {
            console.log('Profile data:', response.data);
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: response.data,
                token,
              },
            });
          } else {
            // Token inválido, remover do localStorage
            localStorage.removeItem('token');
            dispatch({ type: 'AUTH_ERROR' });
          }
        } catch (error) {
          console.error('Erro ao verificar token:', error);
          localStorage.removeItem('token');
          dispatch({ type: 'AUTH_ERROR' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Função de login
  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await apiService.login(credentials.email, credentials.senha);
      
      if (response.success && response.data) {
        const { usuario, token } = response.data;
        
        console.log('Login response:', response.data);
        console.log('Usuario:', usuario);
        
        // Salvar token no localStorage
        localStorage.setItem('token', token);
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: usuario, token },
        });
        
        return { success: true };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, message: response.message || 'Erro no login' };
      }
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, message: error.message || 'Erro no login' };
    }
  };

  // Função de registro
  const register = async (data: RegisterData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await apiService.register(data);
      
      if (response.success) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: true, message: response.message };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, message: response.message || 'Erro no cadastro' };
      }
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, message: error.message || 'Erro no cadastro' };
    }
  };

  // Função de logout
  const logout = () => {
    localStorage.removeItem('token');
    dispatch({ type: 'LOGOUT' });
  };

  // Função para atualizar perfil
  const updateProfile = async (data: Partial<AuthUser>) => {
    try {
      const response = await apiService.updateProfile(data);
      
      if (response.success && response.data && state.user) {
        const updatedUser = { ...state.user, ...response.data };
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message || 'Erro ao atualizar perfil' };
      }
    } catch (error: any) {
      return { success: false, message: error.message || 'Erro ao atualizar perfil' };
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar o contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};

export default AuthContext;
